// ======================================================
// Pocket Accounting - Daily Money Tips
// ======================================================
//
// HOW TO ADD MORE TIPS LATER:
//
// 1. Find the category you want.
// 2. Add another object to either:
//      parent: []
//      kid: []
// 3. Give the new tip a unique ID.
// 4. Save the file.
//
// No dashboard or database changes are required.
//
// You can also create an entirely new category.
// dailyTip.js automatically discovers categories here.
// ======================================================

export const moneyTips = {
  // ====================================================
  // EARNING
  // ====================================================
  earning: {
    label: 'Earning',
    emoji: '💰',

    parent: [
      {
        id: 'earning-parent-001',
        type: 'tip',
        title: 'Connect Money to Effort',
        text: 'When your child earns money, connect the reward to the effort, responsibility, or skill they used to earn it.',
      },
      {
        id: 'earning-parent-002',
        type: 'conversation',
        title: 'Talk About Earning',
        text: 'Ask your child what kind of work they think deserves to be paid and what responsibilities are simply part of helping the family.',
      },
      {
        id: 'earning-parent-003',
        type: 'try',
        title: 'Celebrate the Work',
        text: 'When your child completes a paid chore, recognize the work they did before focusing on the money they earned.',
      },
    ],

    kid: [
      {
        id: 'earning-kid-001',
        type: 'tip',
        title: 'Money Can Be Earned',
        text: 'Money usually comes from effort, skills, and responsibility. Think about what you did to earn your latest money.',
      },
      {
        id: 'earning-kid-002',
        type: 'question',
        title: 'Think About It',
        text: 'What is one job or responsibility you could do really well to earn money?',
      },
      {
        id: 'earning-kid-003',
        type: 'challenge',
        title: 'Earn It!',
        text: 'Pick one responsibility you can complete today and do your best work on it.',
      },
    ],
  },

  // ====================================================
  // SAVING
  // ====================================================
  saving: {
    label: 'Saving',
    emoji: '🐷',

    parent: [
      {
        id: 'saving-parent-001',
        type: 'tip',
        title: 'Save First',
        text: 'Encourage your child to save part of every earning before deciding what they want to spend.',
      },
      {
        id: 'saving-parent-002',
        type: 'conversation',
        title: 'Why Save?',
        text: 'Ask your child what saving money makes possible that spending all of it today would not.',
      },
      {
        id: 'saving-parent-003',
        type: 'try',
        title: 'Build the Habit',
        text: 'The amount saved matters less than building the habit. Even saving $1 from an earning is progress worth recognizing.',
      },
    ],

    kid: [
      {
        id: 'saving-kid-001',
        type: 'challenge',
        title: 'Save First!',
        text: 'Before spending money you earned, put a little toward your savings. Even $1 counts.',
      },
      {
        id: 'saving-kid-002',
        type: 'question',
        title: 'Future You',
        text: 'What could you buy later if you saved some of your money instead of spending all of it today?',
      },
      {
        id: 'saving-kid-003',
        type: 'tip',
        title: 'Small Savings Grow',
        text: 'You do not need to save a lot at once. Small amounts saved again and again can become something big.',
      },
    ],
  },

  // ====================================================
  // NEEDS VS WANTS
  // ====================================================
  needsWants: {
    label: 'Needs vs. Wants',
    emoji: '🤔',

    parent: [
      {
        id: 'needs-parent-001',
        type: 'conversation',
        title: 'Need or Want?',
        text: 'Before a purchase, ask your child whether they think it is a need or a want and let them explain their reasoning.',
      },
      {
        id: 'needs-parent-002',
        type: 'tip',
        title: 'Avoid Giving the Answer',
        text: 'Instead of immediately labeling something a need or want, encourage your child to think through why they believe it belongs in either category.',
      },
      {
        id: 'needs-parent-003',
        type: 'try',
        title: 'Compare Purchases',
        text: 'Choose two everyday purchases and ask your child which one is more important and why.',
      },
    ],

    kid: [
      {
        id: 'needs-kid-001',
        type: 'question',
        title: 'Need or Want?',
        text: 'Think about something you want to buy. Is it something you need or something you want?',
      },
      {
        id: 'needs-kid-002',
        type: 'challenge',
        title: 'Money Detective',
        text: 'Find one need and one want in your life today. What makes them different?',
      },
      {
        id: 'needs-kid-003',
        type: 'tip',
        title: 'Think Before Buying',
        text: 'Wants are not bad. The important part is knowing the difference before deciding where your money goes.',
      },
    ],
  },

  // ====================================================
  // GOAL SETTING
  // ====================================================
  goalSetting: {
    label: 'Goal Setting',
    emoji: '🎯',

    parent: [
      {
        id: 'goal-parent-001',
        type: 'tip',
        title: 'Give Saving a Purpose',
        text: 'Saving becomes more meaningful when your child has something specific they are working toward.',
      },
      {
        id: 'goal-parent-002',
        type: 'conversation',
        title: 'What Are You Saving For?',
        text: 'Ask your child what they would be excited to save for and why that goal matters to them.',
      },
      {
        id: 'goal-parent-003',
        type: 'try',
        title: 'Celebrate Progress',
        text: 'Celebrate milestones along the way to a savings goal instead of waiting until the entire goal is reached.',
      },
    ],

    kid: [
      {
        id: 'goal-kid-001',
        type: 'question',
        title: 'Pick a Goal',
        text: 'If you could save for one thing right now, what would it be?',
      },
      {
        id: 'goal-kid-002',
        type: 'challenge',
        title: 'Get One Step Closer',
        text: 'Put something toward your savings goal today, even if it is only a small amount.',
      },
      {
        id: 'goal-kid-003',
        type: 'tip',
        title: 'Goals Make Saving Easier',
        text: 'Saving can feel easier when you know exactly what you are working toward.',
      },
    ],
  },

  // ====================================================
  // BUDGETING
  // ====================================================
  budgeting: {
    label: 'Budgeting',
    emoji: '📊',

    parent: [
      {
        id: 'budget-parent-001',
        type: 'conversation',
        title: 'Give Every Dollar a Job',
        text: 'Give your child a pretend amount of money and ask how they would divide it between saving and spending.',
      },
      {
        id: 'budget-parent-002',
        type: 'tip',
        title: 'Planning Comes First',
        text: 'Help your child understand that a budget is simply a plan for money before the money is spent.',
      },
      {
        id: 'budget-parent-003',
        type: 'try',
        title: 'Practice With $20',
        text: 'Ask your child how they would use $20 if they wanted to save some, spend some, and still have money left.',
      },
    ],

    kid: [
      {
        id: 'budget-kid-001',
        type: 'question',
        title: 'Your $20 Plan',
        text: 'Imagine you have $20. How much would you save, how much would you spend, and how much would you keep?',
      },
      {
        id: 'budget-kid-002',
        type: 'tip',
        title: 'Make a Money Plan',
        text: 'A budget is just a plan for your money. You decide where your money should go before you spend it.',
      },
      {
        id: 'budget-kid-003',
        type: 'challenge',
        title: 'Plan Before You Spend',
        text: 'Before your next purchase, decide how much money you want to keep afterward.',
      },
    ],
  },

  // ====================================================
  // DELAYED GRATIFICATION
  // ====================================================
  waiting: {
    label: 'Waiting Before Spending',
    emoji: '⏳',

    parent: [
      {
        id: 'waiting-parent-001',
        type: 'tip',
        title: 'Practice the Pause',
        text: 'Encourage your child to wait before making an impulse purchase. Wanting something today does not mean they need to buy it today.',
      },
      {
        id: 'waiting-parent-002',
        type: 'conversation',
        title: 'Would You Still Want It Tomorrow?',
        text: 'When your child wants to buy something immediately, ask whether they think they will still want it tomorrow.',
      },
      {
        id: 'waiting-parent-003',
        type: 'try',
        title: 'Create a Waiting Rule',
        text: 'Try a simple family rule: wait a day before using savings for an unplanned purchase.',
      },
    ],

    kid: [
      {
        id: 'waiting-kid-001',
        type: 'challenge',
        title: 'Wait One Day',
        text: 'Want to buy something today? Try waiting until tomorrow and see if you still want it as much.',
      },
      {
        id: 'waiting-kid-002',
        type: 'question',
        title: 'Worth the Wait?',
        text: 'Would you rather buy something small today or keep saving for something you want even more?',
      },
      {
        id: 'waiting-kid-003',
        type: 'tip',
        title: 'You Can Decide Later',
        text: 'Not buying something right away does not mean you can never buy it. It gives you more time to decide.',
      },
    ],
  },

  // ====================================================
  // TRACKING
  // ====================================================
  tracking: {
    label: 'Tracking Money',
    emoji: '📝',

    parent: [
      {
        id: 'tracking-parent-001',
        type: 'tip',
        title: 'Record It Right Away',
        text: 'Encourage your child to record money when they earn or spend it instead of trying to remember everything later.',
      },
      {
        id: 'tracking-parent-002',
        type: 'conversation',
        title: 'Where Did the Money Go?',
        text: 'Review a few recent transactions together and ask your child what they notice about their spending and saving.',
      },
      {
        id: 'tracking-parent-003',
        type: 'try',
        title: 'Build the Accounting Habit',
        text: 'When your child earns or spends money today, remind them to enter it in Pocket Accounting while the transaction is still fresh.',
      },
    ],

    kid: [
      {
        id: 'tracking-kid-001',
        type: 'challenge',
        title: 'Record It!',
        text: 'Earned or spent money today? Add it to your Piggy Bank so your balance tells the real story.',
      },
      {
        id: 'tracking-kid-002',
        type: 'tip',
        title: 'Keep Track',
        text: 'Tracking your money helps you know exactly what you have instead of guessing.',
      },
      {
        id: 'tracking-kid-003',
        type: 'question',
        title: 'Check Your Money Story',
        text: 'Look at your recent activity. What does it tell you about how you have been using your money?',
      },
    ],
  },

  // ====================================================
  // DECISION MAKING
  // ====================================================
  decisionMaking: {
    label: 'Money Decisions',
    emoji: '🧠',

    parent: [
      {
        id: 'decision-parent-001',
        type: 'conversation',
        title: 'Let Them Decide',
        text: 'Instead of saying “don’t buy that,” ask whether the purchase feels worth using part of their own savings.',
      },
      {
        id: 'decision-parent-002',
        type: 'tip',
        title: 'Small Choices Teach',
        text: 'Allowing children to make small money mistakes can create valuable lessons while the stakes are still low.',
      },
      {
        id: 'decision-parent-003',
        type: 'try',
        title: 'Ask One More Question',
        text: 'Before giving your opinion about a purchase, ask your child what they might be giving up by spending the money.',
      },
    ],

    kid: [
      {
        id: 'decision-kid-001',
        type: 'question',
        title: 'Is It Worth It?',
        text: 'Before buying something, ask yourself: Is this worth giving up part of my savings for?',
      },
      {
        id: 'decision-kid-002',
        type: 'tip',
        title: 'Every Choice Has a Trade-Off',
        text: 'When you spend money on one thing, that money cannot be used for something else. Choose what matters most to you.',
      },
      {
        id: 'decision-kid-003',
        type: 'challenge',
        title: 'Make Your Own Choice',
        text: 'For your next purchase, think of one reason to buy it and one reason to save the money instead.',
      },
    ],
  },

  // ====================================================
  // GIVING
  // ====================================================
  giving: {
    label: 'Giving',
    emoji: '❤️',

    parent: [
      {
        id: 'giving-parent-001',
        type: 'conversation',
        title: 'Money Can Help Others',
        text: 'Introduce the idea that money can be used not only for spending and saving, but also for helping people or causes they care about.',
      },
      {
        id: 'giving-parent-002',
        type: 'tip',
        title: 'Let Giving Be Their Choice',
        text: 'Rather than requiring generosity, help your child discover people, organizations, or causes they genuinely care about.',
      },
      {
        id: 'giving-parent-003',
        type: 'try',
        title: 'Talk About Impact',
        text: 'Ask your child what they would want to help with if they someday chose to give a small part of their money.',
      },
    ],

    kid: [
      {
        id: 'giving-kid-001',
        type: 'question',
        title: 'Who Would You Help?',
        text: 'If you decided to use a little of your money to help someone, who or what would you want to help?',
      },
      {
        id: 'giving-kid-002',
        type: 'tip',
        title: 'Money Can Do Good',
        text: 'Money can buy things and build savings, but it can also help other people.',
      },
      {
        id: 'giving-kid-003',
        type: 'challenge',
        title: 'Think Generously',
        text: 'Think of one way you could help someone today. It does not even have to involve spending money.',
      },
    ],
  },

  // ====================================================
  // REFLECTION
  // ====================================================
  reflection: {
    label: 'Money Reflection',
    emoji: '🌟',

    parent: [
      {
        id: 'reflection-parent-001',
        type: 'conversation',
        title: 'Review the Week',
        text: 'Look together at what your child earned, spent, and saved this week. Focus on what they learned rather than judging each purchase.',
      },
      {
        id: 'reflection-parent-002',
        type: 'tip',
        title: 'Notice Progress',
        text: 'Money skills grow through practice. Recognize good decisions and improving habits, even when they are small.',
      },
      {
        id: 'reflection-parent-003',
        type: 'try',
        title: 'Ask What They Learned',
        text: 'Ask your child one simple question: “What is one money decision you feel good about this week?”',
      },
    ],

    kid: [
      {
        id: 'reflection-kid-001',
        type: 'question',
        title: 'Look Back',
        text: 'What is one money decision you made recently that you feel good about?',
      },
      {
        id: 'reflection-kid-002',
        type: 'challenge',
        title: 'Check Your Progress',
        text: 'Look at what you earned, spent, and saved recently. What would you like to do differently next time?',
      },
      {
        id: 'reflection-kid-003',
        type: 'tip',
        title: 'You Are Learning',
        text: 'You do not have to make perfect money choices. Paying attention to your choices is how you get better.',
      },
    ],
  },
};

export default moneyTips;
